'use client';

import { memo, useCallback, useMemo, useState } from 'react';

import { Button } from '@nextui-org/react';
import { ArrowLeftIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';

import { cn } from '@/shared/utils/utils';

interface PageHeaderProps {
  title: string | React.ReactNode;
  backPath?: URL;
  goBack?: boolean;
  rightContent?: React.ReactNode;
  className?: string;
  subTitle?: string;
  /**
   * Тег заголовка. По умолчанию h1. Передавайте 'h2', если H1 на странице
   * уже есть в другом месте (напр. отдельный мобильный H1 в каталоге),
   * чтобы не дублировать H1 в DOM.
   */
  titleTag?: 'h1' | 'h2';
}

// eslint-disable-next-line react/display-name
export const PageHeader = memo(
  ({
    title,
    backPath: _backPath,
    className,
    goBack = true,
    rightContent,
    subTitle,
    titleTag: TitleTag = 'h1',
  }: PageHeaderProps) => {
    const router = useRouter();
    const [isAffixed] = useState<boolean>();

    const titleMemo = useMemo(() => title, [title]);
    const rightContentMemo = useMemo(() => rightContent, [rightContent]);

    const goBackHandler = useCallback(() => {
      router.back();
    }, [router]);

    return (
      <>
        <div
          className={cn(
            className,
            `flex bg-white ${isAffixed ? 'border-b px-3 absolute w-[101vw] left-1/2 transform -translate-x-1/2 top-0' : ''} lg:items-center justify-between gap-3 flex-col lg:flex-row`,
          )}
        >
          <div
            className={`flex items-center w-full justify-between  ${isAffixed ? 'container' : ''}`}
          >
            <div className="text-xl flex-grow flex-shrink-0  flex gap-3 items-center  font-semibold">
              {goBack && (
                <Button
                  isIconOnly
                  onClick={goBackHandler}
                  variant="light"
                  radius="full"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                  <span className="sr-only">Back</span>
                </Button>
              )}
              <div className="flex flex-col">
                <TitleTag
                  className={`${isAffixed ? 'text-2xl' : 'text-3xl'} transition-all duration-300 text-balance font-bold`}
                >
                  {titleMemo}
                </TitleTag>
                {subTitle && (
                  <div>
                    <span className="text-gray-500 text-sm text-base">
                      {subTitle}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {rightContentMemo}
          </div>
        </div>
      </>
    );
  },
);
